import type { ContentType } from "@brookbush/database";
import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { FaHeart } from "react-icons/fa";

import { useLikes, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import { buttonVariants } from "~/lib/animations.lib";

interface LikeButtonProps {
	contentId: string;
	contentType: ContentType;
	isIconButton?: boolean;
	id?: string;
}

const LikeButton = (props: LikeButtonProps) => {
	const { userId, isLoaded } = useAuth();

	const {
		data: likes,
		isLoadingLikes,
		addLike,
		removeLike,
	} = useLikes({
		contentType: props.contentType,
		excludeContent: true,
	});

	const trackCTA = useTrackCTA();
	const like = likes?.find(
		(like) => like.userId === userId && like.contentId === props.contentId,
	);

	const isLoading = isLoadingLikes || !isLoaded;

	const { setUserAction } = useUserAction();

	return (
		<motion.button
			disabled={isLoading}
			whileTap={buttonVariants.loading}
			id={props.id}
			className={clsx(
				"flex h-min max-w-max shrink-0 items-center justify-center gap-1.5 phablet:gap-2 rounded-full phablet:px-3 px-4 phablet:py-1.5 py-2 font-medium text-base disabled:opacity-75 disabled:brightness-100",
				like
					? "bg-rose-100 text-rose-800 hover:bg-rose-200 dark:bg-rose-400/20 dark:text-rose-200 dark:hover:bg-rose-400/30"
					: "bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:hover:bg-slate-600",
			)}
			onClick={() => {
				if (!userId) {
					trackCTA(UserAction.SIGN_UP, "Add Like");

					setUserAction(UserAction.SIGN_UP);
				} else if (!like) {
					addLike(props.contentId);
				} else {
					removeLike(like.id!);
				}
			}}
		>
			{like ? (
				<>
					<FaHeart className="h-5 w-5 shrink-0" />{" "}
					{!props.isIconButton && (
						<span className="phablet:block hidden"> Unlike</span>
					)}
				</>
			) : (
				<>
					<Heart className="h-5 w-5 shrink-0" />{" "}
					{!props.isIconButton && (
						<span className="phablet:block hidden"> Like</span>
					)}
				</>
			)}
		</motion.button>
	);
};

export default LikeButton;
